'use client'

import { useCallback, useEffect, useRef } from 'react'

import {
  ADD_TO_CART_PRICE_TYPE,
  CTA_POSITION_INLINE_BUY_BUTTON,
  NO_APLICA,
  resolveCtaDestination,
} from './constants'
import { toSelectItem } from './mapProductToAnalyticsItem'
import type {
  AddToCartParams,
  EcommerceItem,
  ProductListAnalytics,
  SelectItemParams,
  ViewItemListParams,
} from './types'

/**
 * Ventana de agrupación de impresiones. La primera card que cruza el umbral
 * abre la ventana; todas las que lo crucen dentro de ella viajan en el mismo
 * `view_item_list`. Sin esto, un scroll normal emitiría un evento por card.
 */
export const IMPRESSION_BATCH_MS = 300

export interface ProductListTracking {
  /**
   * Registra la impresión de una card. `key` identifica el producto para la
   * deduplicación (normalmente su `id`): la primera impresión cuenta, las
   * siguientes se ignoran mientras el componente siga montado.
   */
  readonly trackImpression: (key: string, item: EcommerceItem) => void
  /** Emite el `select_item` de la card clicada (un único ítem). */
  readonly trackSelect: (item: EcommerceItem) => void
  /**
   * Emite el `add_to_cart` del CTA «Comprar» de una card (`Comprar.csv` →
   * «directo desde tarjeta»). `purchaseUrl` es el destino real del botón.
   */
  readonly trackPurchase: (item: EcommerceItem, purchaseUrl: string | null | undefined) => void
}

/**
 * Los dos trackers de e-commerce que consume el hook, inyectados por el
 * llamador en vez de leerse aquí de `useOptionalPageAnalytics()`: el chrome
 * (`SearchInput` — "Productos más buscados") solo tiene
 * `useOptionalChromeAnalytics()`, no `PageAnalyticsContext`
 * (`ChromeAnalyticsContext.tsx`), así que el hook no puede asumir una única
 * fuente. `undefined` es un no-op válido, mismo criterio que "fuera del
 * provider" en el resto de la instrumentación (p. ej. `analytics?.trackX`).
 */
export interface ProductListTrackers {
  readonly trackViewItemList?: (params: ViewItemListParams) => void
  readonly trackSelectItem?: (params: SelectItemParams) => void
  readonly trackAddToCart?: (params: AddToCartParams) => void
}

/**
 * `useProductListTracking` — eventos de e-commerce de UN listado de productos:
 * `view_item_list` (impresiones agrupadas y deduplicadas) y `select_item`.
 *
 * Vive en `lib/analytics/` y no en una página porque lo comparten los tres
 * tipos de listado del sitio: el listado principal de una PLP (virtualizado,
 * con scroll infinito), la franja "Eventos en otras ciudades" y el carril de
 * "Vistos recientemente".
 *
 * La **deduplicación vive en el hook, no en la card**: `VirtualizedList`
 * desmonta del DOM las filas fuera de rango, así que el `IntersectionObserver`
 * de cada card se destruye y se recrea al subir/bajar. Sin este registro, cada
 * pasada volvería a contar la misma impresión. Solo se reinicia cuando el
 * componente que llama al hook se desmonta — que es justo cuando cambian
 * location/filtros y una impresión nueva SÍ es legítima.
 *
 * Sin `trackViewItemList`/`trackSelectItem` (ninguno de los dos trackers, o el
 * provider correspondiente devuelve `null` — páginas aún sin instrumentar,
 * tests) no emite nada y no falla.
 */
export function useProductListTracking(
  { itemListId, itemListName, moduleName, moduleType, modulePosition }: ProductListAnalytics,
  trackers: ProductListTrackers,
): ProductListTracking {
  const seenKeysRef = useRef<Set<string>>(new Set())
  const pendingItemsRef = useRef<EcommerceItem[]>([])
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Los trackers se leen vía ref (actualizada en un efecto, nunca durante el
  // render) para que `flushImpressions`/`trackSelect` no se recreen —y por
  // tanto no reprogramen la ventana de agrupación— en cada render del
  // llamador; mismo criterio que `onImpressionRef` en `TrackedProductCard`.
  const trackersRef = useRef(trackers)
  useEffect(() => {
    trackersRef.current = trackers
  })

  const flushImpressions = useCallback(() => {
    flushTimerRef.current = null
    const batch = pendingItemsRef.current
    pendingItemsRef.current = []
    if (batch.length === 0) return
    // Orden de lectura: el diccionario pide "todos los ítems visibles con su
    // índice correcto", y el observer no garantiza el orden de las entradas.
    batch.sort((a, b) => a.index - b.index)
    trackersRef.current.trackViewItemList?.({
      item_list_id: itemListId,
      item_list_name: itemListName,
      module_name: moduleName,
      module_type: moduleType,
      module_position: modulePosition,
      items: batch,
    })
  }, [itemListId, itemListName, moduleName, moduleType, modulePosition])

  const trackImpression = useCallback(
    (key: string, item: EcommerceItem) => {
      if (seenKeysRef.current.has(key)) return
      seenKeysRef.current.add(key)
      pendingItemsRef.current.push(item)
      // Ventana de agrupación: se programa una sola vez y no se reprograma, para
      // que un scroll continuo no la aplace indefinidamente.
      if (flushTimerRef.current) return
      flushTimerRef.current = setTimeout(flushImpressions, IMPRESSION_BATCH_MS)
    },
    [flushImpressions],
  )

  const trackSelect = useCallback(
    (item: EcommerceItem) => {
      // `item_list_id`/`item_list_name` e `index` son los mismos del
      // `view_item_list` previo — el diccionario los exige para el funnel.
      trackersRef.current.trackSelectItem?.({
        item_list_id: itemListId,
        item_list_name: itemListName,
        module_name: moduleName,
        module_type: moduleType,
        // `no_aplica` en `select_item` (a diferencia de `view_item_list`), según CSV.
        module_position: NO_APLICA,
        items: [toSelectItem(item)],
      })
    },
    [itemListId, itemListName, moduleName, moduleType],
  )

  const trackPurchase = useCallback(
    (item: EcommerceItem, purchaseUrl: string | null | undefined) => {
      const { host, vendor } = resolveCtaDestination(purchaseUrl)
      trackersRef.current.trackAddToCart?.({
        currency: item.currency,
        value: item.price,
        item_list_id: itemListId,
        item_list_name: itemListName,
        cta_position: CTA_POSITION_INLINE_BUY_BUTTON,
        cta_destination_host: host,
        cta_destination_vendor: vendor,
        items: [{ ...item, price_type: ADD_TO_CART_PRICE_TYPE }],
      })
    },
    [itemListId, itemListName],
  )

  // Al desmontar se descarta el lote pendiente en vez de vaciarlo: el remontaje
  // lo provoca un cambio de location/filtros, así que emitirlo tarde lo
  // atribuiría a una página que ya no es la que generó esas impresiones.
  useEffect(
    () => () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current)
    },
    [],
  )

  return { trackImpression, trackSelect, trackPurchase }
}
